import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./orgContextMenu.html";
import { WireStore } from '@oneappexchange/lwc-wired';
import equalityFn from 'zustand/shallow';
import { createEvent } from '~/modules/analytics/analyticsHelper';
import { clearAuthenticationContext } from '~/modules/analytics/authenticationContext';
import { ElementType } from '~/modules/analytics/gtmEventTypes/constants';
import { APPX_JP_SITE_URL, APPX_SITE_URL, IS_DEVELOPMENT, TBID_MOCK_USER, TBID_URL } from '~/modules/config';
import { getUserLanguage } from '~/modules/helpers/languageHelper';
import { getOrgEditionBadgeTheme, getOrgName, hasOrgDomain, hasOrgEdition } from '~/modules/helpers/orgCardFields';
import { switchToOrgPicker } from '~/modules/helpers/orgSwitchDropOff';
import { generateBaseUrl, logout } from '~/modules/helpers/routingHelper';
import { Routes } from '~/modules/routes';
import { i18nStore, selectI18nModule } from '~/modules/stores/i18nStore';
import { orgContextStore, selectSelectedOrg } from '~/modules/stores/orgContextStore';
import { orgPickerStore } from '~/modules/stores/orgPickerStore';
import { selectIsAgentExchange, siteConfigStore } from '~/modules/stores/siteConfigStore';
import { userStore } from '~/modules/stores/userStore';
import { createCookie } from '~/modules/utilities/cookies';
import { ENGLISH_LANGUAGE_CODE, JAPANESE_LANGUAGE_CODE } from '~/modules/utilities/language_utils';
import { isOrgPickerEnabled } from '~/modules/utilities/orgPickerPilot';
import { getValidatedSelectedOrg } from '~/modules/helpers/orgContextHelper';
// Re-enable the menu this long after a logout click, as a fallback if logout() never navigates
// away (errors/stalls). A successful logout tears the component down before this fires.
const LOGOUT_FALLBACK_MS = 10000;
/**
 * Header menu for multi-org users: the selected org is the trigger; the dropdown holds its card, a
 * "Switch org" link to /select-org, and the account links. Mounted (by the header) only for multi-org
 * users under the shared org-picker gate (FF_ORG_PICKER or pilot enrollment, English-only);
 * single-org users keep ace-user-menu. The account links mirror ace-user-menu rather than share
 * code, so the user menu stays untouched.
 */
class OrgContextMenu extends LightningElement {
  constructor(...args) {
    super(...args);
    this.i18n = void 0;
    this.selectedOrg = void 0;
    this.user = void 0;
    this.expandedMenu = false;
    this._shouldFocusDropdown = false;
    this.isTriggerTextTruncated = false;
    // The org label last measured for overflow. Truncation is only re-measured when the label
    // changes — not on every render — so toggling isTriggerTextTruncated can't re-trigger a measure
    // and thrash the layout (wrapping the label in ax-tooltip changes the DOM but not the label).
    this.lastMeasuredLabel = null;
    // Latched true from the logout click until teardown; keeps the stale "logged in" menu closed and
    // non-interactive while the async SFIDWidget.logout() settles.
    this.isLoggingOut = false;
    this.handleDocumentClick = event => {
      if (!this.expandedMenu) {
        return;
      }
      const dropdown = this.template?.querySelector('div[data-role="dropdown"]');
      const trigger = this.template?.querySelector('button[data-role="trigger"]');
      const composedPath = typeof event.composedPath === 'function' ? event.composedPath() : [];
      const target = event.target;
      const clickInsideDropdown = dropdown && composedPath.includes(dropdown) || !!dropdown && !!target && dropdown.contains(target);
      const clickInsideTrigger = trigger && composedPath.includes(trigger) || !!trigger && !!target && trigger.contains(target);
      if (!clickInsideDropdown && !clickInsideTrigger) {
        this.toggleMenu();
      }
    };
  }
  // Backstop to the header's mount decision: mirrors the same shared gate (flag or pilot-enrolled,
  // English-only), plus i18n readiness so the trigger labels don't render before their strings exist.
  get showOrgContext() {
    return isOrgPickerEnabled() && Boolean(this.i18n);
  }
  get hasSelectedOrg() {
    return Boolean(this.validatedSelectedOrg);
  }
  get validatedSelectedOrg() {
    return getValidatedSelectedOrg(this.selectedOrg, orgPickerStore.getState());
  }

  // The selected org's name, or the "Select Org" prompt when nothing is chosen yet.
  get triggerLabel() {
    return this.validatedSelectedOrg?.name || this.validatedSelectedOrg?.username || this.i18n.orgContextMenu.selectOrg();
  }
  get orgName() {
    return getOrgName(this.validatedSelectedOrg);
  }
  get hasEdition() {
    return hasOrgEdition(this.validatedSelectedOrg);
  }
  get hasDomain() {
    return hasOrgDomain(this.validatedSelectedOrg);
  }
  get editionBadgeTheme() {
    return getOrgEditionBadgeTheme(this.validatedSelectedOrg);
  }
  get triggerAriaLabel() {
    return this.i18n.orgContextMenu.triggerAriaLabel({
      ORG_NAME: this.orgName
    });
  }
  get greeting() {
    return this.i18n.orgContextMenu.greeting({
      FIRST_NAME: this.user?.tbidProfile?.given_name ?? ''
    });
  }
  get userInitial() {
    const name = this.user?.tbidProfile?.given_name;
    return name ? name.charAt(0).toUpperCase() : '';
  }
  get profileImage() {
    return this.user?.tbidProfile?.picture ?? '';
  }

  // Show the profile photo when available; fall back to the initial when it isn't.
  get hasProfileImage() {
    return Boolean(this.profileImage);
  }
  get profileUrl() {
    return `${TBID_URL}/id`;
  }

  // Labels bound directly in the template must be resolved here — LWC templates render a value,
  // they don't call the ICU message functions, so binding the function directly prints its source.
  get profileLabel() {
    return this.i18n.header.profile();
  }
  get switchOrgLabel() {
    return this.i18n.orgContextMenu.switchOrg();
  }
  get selectOrgLabel() {
    return this.i18n.orgContextMenu.selectOrg();
  }
  get orgIconDescription() {
    return this.i18n.orgContextMenu.orgIconDescription();
  }
  get closeLabel() {
    return this.i18n.shared.close();
  }
  get menuClassName() {
    return 'dropdown-menu' + (this.expandedMenu ? ' expanded' : '');
  }

  // Saved-listings (favorites) URL — same logic as ace-favorites: site-relative on AppExchange,
  // absolute to the AppExchange site (EN/JP) on AgentExchange where favorites don't live locally.
  get favoritesUrl() {
    if (selectIsAgentExchange(siteConfigStore.getState())) {
      const baseUrl = getUserLanguage() === JAPANESE_LANGUAGE_CODE ? APPX_JP_SITE_URL : APPX_SITE_URL;
      return `${baseUrl}/${Routes.FAVORITE_LISTINGS}`;
    }
    return `/${Routes.FAVORITE_LISTINGS}`;
  }

  // Account links below the org card, mirroring ace-favorites / ace-user-menu. Order matches Figma.
  get menuItems() {
    const items = [{
      id: 'favorites',
      text: this.i18n.header.favoritesAltText(),
      link: this.favoritesUrl
    }];
    if (getUserLanguage() === ENGLISH_LANGUAGE_CODE && this.user?.canCheckout && this.user?.canInstall) {
      items.push({
        id: 'myInstalls',
        text: this.i18n.header.myInstalls(),
        link: `${generateBaseUrl()}/appxMyAccount`
      });
    }
    items.push({
      id: 'settings',
      text: this.i18n.header.settings(),
      link: `${TBID_URL}/settings`
    });
    items.push({
      id: 'logout',
      text: this.i18n.header.logout(),
      link: '#'
    });
    return items;
  }
  connectedCallback() {
    document.addEventListener('mousedown', this.handleDocumentClick);
  }
  disconnectedCallback() {
    document.removeEventListener('mousedown', this.handleDocumentClick);
    clearTimeout(this.logoutFallbackTimer);
  }
  renderedCallback() {
    if (this._shouldFocusDropdown) {
      this.focusDropdown();
      this._shouldFocusDropdown = false;
    }
    this.checkTriggerTruncation();
  }
  checkTriggerTruncation() {
    // Nothing renders (and triggerLabel reads this.i18n) until showOrgContext is true, so bail
    // before touching the label on early renders where i18n isn't ready yet.
    if (!this.showOrgContext) {
      return;
    }
    // Only re-measure when the label actually changed. The measurement itself is stable
    // (scrollWidth/clientWidth on .trigger-text), but gating on the label is what stops flipping
    // isTriggerTextTruncated — which re-renders the span into/out of ax-tooltip — from looping
    // back into another measure and thrashing the layout. Run these cheap checks before the DOM
    // query so an unchanged label bails out without touching the template.
    if (this.triggerLabel === this.lastMeasuredLabel) {
      return;
    }
    // Locate via the always-present wrapper, then measure the .trigger-text inside it.
    // querySelector finds that span whether it's bare or nested in ax-tooltip.
    const wrap = this.template?.querySelector('[data-role="trigger-text-wrap"]');
    if (!wrap) {
      return;
    }
    this.lastMeasuredLabel = this.triggerLabel;
    // scrollWidth includes text clipped by overflow: hidden, so it reports the natural (untruncated)
    // width; clientWidth is the visible box. Wider content than box === the name is truncated.
    const textEl = wrap.querySelector('.trigger-text');
    this.isTriggerTextTruncated = textEl ? textEl.scrollWidth > textEl.clientWidth : false;
  }
  handleSwitchOrg(event) {
    event.preventDefault();
    this.dispatchEvent(createEvent({
      eventType: 'click',
      elementType: ElementType.ORG_PICKER_SWITCH_ORG,
      surface: 'desktop'
    }));
    // Close directly rather than via toggleMenu() so this doesn't also fire an ORG_PICKER_DISMISS
    // event — switching orgs is a deliberate action, not an abandon (mirrors the logout close path).
    this.expandedMenu = false;
    switchToOrgPicker();
  }
  handleClose() {
    this.toggleMenu();
    this.focusTrigger();
  }
  handleMenuItemClick(event) {
    const link = event.target?.closest?.('a');
    if (!link || link.getAttribute('data-testid') !== 'logout') {
      return;
    }
    event.preventDefault();
    // logout() is async: latch a flag to close the menu and block re-clicks. userStore is NOT torn
    // down here — the prod onLogout callback still needs the live userId for its audit log.
    if (this.isLoggingOut) {
      return;
    }
    this.isLoggingOut = true;
    this.expandedMenu = false;
    // Fallback in case logout stalls without navigating, so the menu can't stay disabled forever.
    this.logoutFallbackTimer = setTimeout(() => {
      this.isLoggingOut = false;
    }, LOGOUT_FALLBACK_MS);
    // The persisted selection is intentionally kept across logout — it's what lets the 7-day
    // cookie (W-22903169) auto-reselect on the next login. A different user logging in on the
    // same tab is guarded separately: header.ts and selectOrg.ts call isStaleSelectedOrg()
    // once their own org list resolves; each caller clears the
    // selection itself via orgContextStore.clearSelectedOrg() when the old orgId isn't in it.
    if (IS_DEVELOPMENT && TBID_MOCK_USER) {
      userStore.reset();
      clearAuthenticationContext();
      createCookie('sid', '', -1);
    } else {
      logout();
    }
  }
  handleTriggerClick() {
    this.dispatchEvent(createEvent({
      eventType: 'click',
      elementType: ElementType.ORG_PICKER_BADGE
    }));
    this.toggleMenu();
    this._shouldFocusDropdown = true;
  }

  // ArrowDown/Enter on the trigger opens the panel and moves focus into it; from there, native
  // Tab order carries focus through all controls (this is a disclosure panel, not an ARIA menu).
  handleTriggerKeyDown(event) {
    if (event.key === 'ArrowDown' || event.key === 'Enter') {
      this.toggleMenu();
      this._shouldFocusDropdown = true;
      event.preventDefault();
    }
  }

  // Escape closes the panel and returns focus to the trigger; everything else is left to native
  // Tab order. No arrow-key roving — the panel is a plain set of links, not an ARIA menu.
  handleMenuKeyDown(event) {
    if (event.key === 'Escape') {
      this.toggleMenu();
      this.focusTrigger();
    }
  }
  toggleMenu() {
    // Don't reopen the menu while logout is in flight.
    if (this.isLoggingOut) {
      this.expandedMenu = false;
      return;
    }
    const opening = !this.expandedMenu;
    this.expandedMenu = opening;
    if (opening) {
      this.dispatchEvent(createEvent({
        eventType: 'click',
        elementType: ElementType.ORG_PICKER_DROPDOWN
      }));
    } else {
      // Symmetric partner to the open event above. Firing on the close transition here covers
      // every dismiss path — the X (handleClose), Escape (handleMenuKeyDown), and outside-click
      // (handleDocumentClick) all close via toggleMenu() — without instrumenting each one.
      this.dispatchEvent(createEvent({
        eventType: 'click',
        elementType: ElementType.ORG_PICKER_DISMISS
      }));
    }
  }
  focusDropdown() {
    this.template?.querySelector('div[data-role="dropdown"]')?.focus();
  }
  focusTrigger() {
    this.template?.querySelector('button[data-role="trigger"]')?.focus();
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(OrgContextMenu, {
  wire: {
    i18n: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: i18nStore,
          selector: selectI18nModule
        };
      }
    },
    selectedOrg: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: orgContextStore,
          selector: selectSelectedOrg
        };
      }
    },
    user: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: userStore,
          selector: state => ({
            canInstall: state.canInstall,
            canCheckout: state.canCheckout,
            tbidProfile: state.tbidProfile
          }),
          equalityFn
        };
      }
    }
  },
  fields: ["expandedMenu", "_shouldFocusDropdown", "isTriggerTextTruncated", "lastMeasuredLabel", "isLoggingOut", "handleDocumentClick"]
});
const __lwc_component_class_internal = _registerComponent(OrgContextMenu, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;